import { Input as AntInput, type InputRef } from 'antd';
import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import {
  InputClearAction,
  type InputFieldBaseProps,
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

export type TextInputSize = 'large' | 'medium';

export type TextInputProps = InputFieldBaseProps &
  Partial<{
    maxLength: number;
    size: TextInputSize;
  }>;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntInput = forwardRef<InputRef, any>((props, ref) => (
  <AntInput {...props} ref={ref} />
));

const StyledInput = styled(ForwardedAntInput)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
  $size: TextInputSize;
}>`
  &&.ant-input {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0 ${theme.dime.spacing.l};
    height: ${({ $size }) =>
      $size === 'medium'
        ? theme.dime.spacing['2xl']
        : theme.dime.spacing['3xl']};
    box-shadow: none;
    margin-bottom: ${theme.dime.spacing.m};
  }

  &&.ant-input:disabled {
    cursor: not-allowed;
  }

  &&.ant-input:disabled::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-input[readonly] {
    cursor: not-allowed;
  }
`;

export const TextInput = forwardRef<InputRef, TextInputProps>(
  (
    {
      placeholder = '',
      disabled = false,
      readOnly = false,
      className = '',
      id,
      'data-testid': testId,
      status = 'default',
      size = 'large',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      clearAction = false,
      onClear,
      ...rest
    },
    ref
  ) => {
    const hasError = status === 'error';
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <InputWrapper className={className}>
        {!!label && (
          <InputLabel
            label={label}
            htmlFor={inputId}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledInput
          ref={ref}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          id={inputId}
          data-testid={testId ?? 'text-input'}
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          $size={size}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
        {!!clearAction && !disabled && !readOnly && (
          <InputClearAction onClear={onClear} />
        )}
      </InputWrapper>
    );
  }
);

TextInput.displayName = 'TextInput';
